/**
 * Pure helpers for the booth check-in page, extracted so the data-integrity
 * rules can be unit-tested independently of the React component.
 */

// Booth-facing event-type labels — the raw DB enum (show / class / general_use…)
// leaks implementation language to a non-technical rep. Map to friendly names;
// fall back to Title Case for anything unmapped.
export const EVENT_TYPE_LABELS: Record<string, string> = {
  show: "Gun Show",
  conversion_event: "Conversion Event",
  partner_event: "Partner Event",
  general_use: "General Use",
  class: "Class",
  seminar: "Seminar",
  speaking: "Speaking",
  networking: "Networking",
  table: "Table",
};

export function eventTypeLabel(type: string | null | undefined): string {
  if (!type) {
    return "—";
  }
  return (
    EVENT_TYPE_LABELS[type] ||
    type.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase())
  );
}

/**
 * The outcome of a submit attempt, enough to decide whether the form should be
 * cleared for the next member. The governing rule (audit findings #1 + #2): NEVER
 * reset while a wanted friend-link is still pending — that would silently discard
 * the typed friend identifier and read as if the whole action was saved.
 */
export type CheckinFlowOutcome =
  /** Offline: check-in queued. friendJoined ⇒ a link is still pending. */
  | { kind: "offline_queued"; friendJoined: boolean }
  /** Offline: queue was full — nothing saved. */
  | { kind: "offline_full" }
  /** Online: check-in succeeded. linkOk is true when no link was wanted OR it linked. */
  | { kind: "online_success"; linkOk: boolean }
  /** Online: network failure, check-in queued. wantsLink ⇒ link still pending. */
  | { kind: "online_network_queued"; wantsLink: boolean }
  /** Online: network failure and the queue was full. */
  | { kind: "online_network_full" }
  /** Online: a business/validation failure — nothing saved. */
  | { kind: "online_failed" };

/**
 * Whether to clear the form and advance to the next member. We ONLY reset on a
 * fully-clean outcome (check-in saved AND any wanted friend-link completed). Every
 * partial/pending/failed case keeps the form so the rep's input survives.
 */
export function shouldResetAfterCheckin(outcome: CheckinFlowOutcome): boolean {
  switch (outcome.kind) {
    case "offline_queued":
      return !outcome.friendJoined;
    case "online_success":
      return outcome.linkOk;
    case "online_network_queued":
      return !outcome.wantsLink;
    case "offline_full":
    case "online_network_full":
    case "online_failed":
      return false;
  }
}

/**
 * Whether to collapse the event selector to a compact summary. Collapse ONLY a
 * verified-eligible chosen event (so guards for ineligible/unknown/engine-load-error
 * stay expanded and visible), and never while the user has tapped "Change" to expand.
 */
export function shouldCollapseSelector(s: {
  haveEligibilityData: boolean;
  hasSelectedEvent: boolean;
  selectedIneligible: boolean;
  expanded: boolean;
}): boolean {
  return (
    s.haveEligibilityData &&
    s.hasSelectedEvent &&
    !s.selectedIneligible &&
    !s.expanded
  );
}

/** Counts for the queue-inspection panel — failed shown DISTINCTLY from pending. */
export interface QueueSummary {
  total: number;
  /** pending + retrying — still expected to sync. */
  pending: number;
  /** terminal until manually retried — needs the rep's attention. */
  failed: number;
}

export function summarizeQueue(entries: { status: string }[]): QueueSummary {
  let pending = 0;
  let failed = 0;
  for (const e of entries) {
    if (e.status === "failed") {
      failed += 1;
    } else {
      pending += 1; // pending | retrying
    }
  }
  return { total: entries.length, pending, failed };
}

/**
 * Compact relative age ("just now" / "5m" / "3h" / "2d") for a queued item.
 * `nowMs` is injected so this stays pure and testable.
 */
export function relativeAge(thenMs: number, nowMs: number): string {
  const sec = Math.max(0, Math.floor((nowMs - thenMs) / 1000));
  if (sec < 45) {
    return "just now";
  }
  const min = Math.floor(sec / 60);
  if (min < 60) {
    return `${Math.max(1, min)}m`;
  }
  const hr = Math.floor(min / 60);
  if (hr < 24) {
    return `${hr}h`;
  }
  return `${Math.floor(hr / 24)}d`;
}
